import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMobileMenuOpen]);

  const navLinkClass = isScrolled
    ? "text-foreground-700 hover:text-foreground-950"
    : "text-white/90 hover:text-white";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
            : "bg-transparent"
        }`}
      >
        <div className="px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6 md:gap-8">
              <Link
                to="/"
                className={`font-heading text-xl tracking-tight whitespace-nowrap cursor-pointer transition-colors ${
                  isScrolled
                    ? "text-foreground-950"
                    : "text-white"
                }`}
              >
                pandabuy<span className="text-primary-500">.help</span>
              </Link>

              <div className="hidden md:flex items-center gap-1">
                <Link
                  to="/"
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${navLinkClass}`}
                >
                  Home
                </Link>
                <Link
                  to="/blog/pandabuy-spreadsheet-guide"
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${navLinkClass}`}
                >
                  Shopping Guide
                </Link>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <Link
                to="/blog/pandabuy-spreadsheet-guide"
                className={`hidden md:flex items-center text-sm transition-colors whitespace-nowrap cursor-pointer ${navLinkClass}`}
              >
                Blog
              </Link>

              <div className="relative hidden md:block" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    isScrolled
                      ? "border-background-200 bg-background-50 text-foreground-800 hover:border-foreground-300"
                      : "border-white/50 bg-white/15 backdrop-blur-sm text-white hover:border-white/80"
                  }`}
                >
                  <i className="ri-grid-line"></i>
                  <span>Categories</span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 text-xs ${isDropdownOpen ? "rotate-180" : ""}`}></i>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-secondary-50 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-foreground-400`}></i>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  isScrolled
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "bg-white text-foreground-950 hover:bg-white/90"
                }`}
              >
                Buy It Now
              </a>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                  isScrolled
                    ? "text-foreground-800 hover:bg-background-100"
                    : "text-white hover:bg-white/10"
                }`}
                aria-label="Toggle menu"
              >
                <i className={`text-lg ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
          <div className="absolute top-16 right-0 bottom-0 w-80 max-w-[85vw] bg-background-50 overflow-y-auto">
            <div className="p-5">
              <div className="mb-6">
                <Link
                  to="/"
                  className="flex items-center gap-3 py-3 text-foreground-800 font-medium text-sm cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                  Home
                </Link>
                <Link
                  to="/blog/pandabuy-spreadsheet-guide"
                  className="flex items-center gap-3 py-3 text-foreground-800 font-medium text-sm cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-book-open-line w-5 h-5 flex items-center justify-center"></i>
                  Shopping Guide
                </Link>
                <Link
                  to="/blog/pandabuy-spreadsheet-guide"
                  className="flex items-center gap-3 py-3 text-foreground-800 font-medium text-sm cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-article-line w-5 h-5 flex items-center justify-center"></i>
                  Blog
                </Link>
              </div>

              <div className="border-t border-background-200/70 pt-4">
                <h4 className="text-xs uppercase tracking-wider text-foreground-400 mb-3 font-label">
                  All Categories
                </h4>
                <div className="flex flex-col">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <i className={`${cat.icon} w-5 h-5 flex items-center justify-center text-foreground-400`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}